/**
 * OI Wall Map (Butterfly chart)
 * CE OI bars → upward (dark red = Resistance), PE OI bars → downward (dark green = Support)
 * Spot price vertical line (amber), Max pain (purple)
 * Bar intensity is shaded by OI magnitude relative to max.
 */
import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer, Cell } from 'recharts';
import { Box, Typography } from '@mui/material';
import { useAppTheme } from '../../hooks/useAppTheme';

interface ChainRow {
  strike: number;
  ceOi: number;
  peOi: number;
  spotPrice?: number;
}

interface Props {
  data: ChainRow[];
  spotPrice: number;
  maxPainStrike?: number;
}

// Support = dark green shades (PE OI)
const SUPPORT_DARK   = '#1a7a2e';
const SUPPORT_MID    = '#2d9e42';
const SUPPORT_LIGHT  = '#4cbf60';

// Resistance = dark red shades (CE OI)
const RESIST_DARK    = '#8b0000';
const RESIST_MID     = '#b52020';
const RESIST_LIGHT   = '#d94040';

function oiShade(value: number, max: number, darkColor: string, midColor: string, lightColor: string): string {
  if (max === 0) return lightColor;
  const ratio = Math.abs(value) / max;
  if (ratio >= 0.66) return darkColor;
  if (ratio >= 0.33) return midColor;
  return lightColor;
}

const OIWallMap: React.FC<Props> = ({ data, spotPrice, maxPainStrike }) => {
  const { C } = useAppTheme();
  if (!data || data.length === 0) return null;

  const sorted = [...data].sort((a, b) => Number(a.strike) - Number(b.strike));

  // If spot is known, focus on ATM ± 10 strikes; otherwise show middle 20 strikes
  let window: typeof sorted;
  if (spotPrice > 0) {
    const dists = sorted.map(r => Math.abs(Number(r.strike) - spotPrice));
    const minDist = Math.min(...dists);
    const atmIdx = dists.findIndex(d => d === minDist);
    window = sorted.slice(Math.max(0, atmIdx - 10), atmIdx + 11);
  } else {
    const mid = Math.floor(sorted.length / 2);
    window = sorted.slice(Math.max(0, mid - 10), mid + 11);
  }

  const chartData = window.map(r => ({
    strike: r.strike,
    CE_OI: Number(r.ceOi) / 1000,   // thousands, upward
    PE_OI: -Number(r.peOi) / 1000   // thousands, downward (negative)
  }));

  const maxCeOi = Math.max(...chartData.map(d => d.CE_OI));
  const maxPeOi = Math.max(...chartData.map(d => Math.abs(d.PE_OI)));

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        OI Wall Map — CE (↑ Resistance) vs PE (↓ Support)
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, bgcolor: RESIST_DARK, borderRadius: 0.5 }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>CE OI — Resistance (dark red = stronger)</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, bgcolor: SUPPORT_DARK, borderRadius: 0.5 }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>PE OI — Support (dark green = stronger)</Typography>
        </Box>
      </Box>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.chartGrid} opacity={0.4} />
          <XAxis dataKey="strike" angle={-45} textAnchor="end" tick={{ fontSize: 10 }} tickCount={8} />
          <YAxis tickFormatter={v => `${Math.abs(v)}K`} tick={{ fontSize: 10 }} />
          <Tooltip
            formatter={(val: any, name: string) => [
              `${Math.abs(Number(val)).toFixed(0)}K lots`,
              name === 'CE_OI' ? 'CE OI (Resistance)' : 'PE OI (Support)'
            ]}
            labelFormatter={(label) => `Strike: ₹${label}`}
            contentStyle={{ backgroundColor: C.chartTooltipBg, border: `1px solid ${C.chartTooltipBorder}` }}
          />
          <ReferenceLine y={0} stroke={C.chartGrid} strokeWidth={1} />
          <ReferenceLine x={spotPrice} stroke={C.spot} strokeWidth={2} label={{ value: 'Spot', fill: C.spot, fontSize: 11 }} />
          {maxPainStrike && (
            <ReferenceLine x={maxPainStrike} stroke={C.maxPain} strokeWidth={2} strokeDasharray="4 4"
              label={{ value: 'MaxPain', fill: C.maxPain, fontSize: 11 }} />
          )}
          <Bar dataKey="CE_OI" radius={[2, 2, 0, 0]} maxBarSize={20}>
            {chartData.map((d, i) => (
              <Cell key={i} fill={oiShade(d.CE_OI, maxCeOi, RESIST_DARK, RESIST_MID, RESIST_LIGHT)} />
            ))}
          </Bar>
          <Bar dataKey="PE_OI" radius={[0, 0, 2, 2]} maxBarSize={20}>
            {chartData.map((d, i) => (
              <Cell key={i} fill={oiShade(d.PE_OI, maxPeOi, SUPPORT_DARK, SUPPORT_MID, SUPPORT_LIGHT)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default OIWallMap;
